<template>
  <div
    class="testimonial"
    :class="variant"
  >
    <div
      v-if="!!testimonial.logo"
      class="head"
    >
      <UiComponentImage
        :alt="testimonial.logo.alt"
        class="logo"
        :expected-size="24"
        :extra-params="{ trim: 'color' }"
        :height="testimonial.logo.height"
        orientation="portrait"
        :source="testimonial.logo.url"
        use-natural-height
        use-natural-width
        :width="testimonial.logo.width"
      />
    </div>
    <div
      v-if="withStars"
      class="rating"
    >
      <img
        v-for="n in 4"
        :key="n"
        alt="star"
        height="20"
        loading="lazy"
        src="@/assets/svg/star.svg"
        width="20"
      >
      <img
        alt="star"
        height="20"
        loading="lazy"
        src="@/assets/svg/star-not-complete.svg"
        width="20"
      >
    </div>
    <UiComponentParagraph class="body">
      {{ quotationMarkStart }}{{ testimonial.quote
      }}{{ quotationMarkEnd }}
    </UiComponentParagraph>
    <AtomButton
      v-if="testimonial.slug"
      :to="testimonial.slug"
      type="text"
    >
      <slot /><UiComponentIcon type="arrow-right" />
    </AtomButton>
    <div
      class="footer"
      :class="{ compact: !!testimonial.slug }"
    >
      <div class="customer">
        <div class="left">
          <UiComponentImage
            v-if="!!testimonial.customer && testimonial.customer.avatar"
            :alt="testimonial.customer.avatar.alt"
            :expected-size="avatarSize"
            :height="avatarSize"
            :source="testimonial.customer.avatar.url"
            :width="avatarSize"
          />
        </div>
        <div class="right">
          <UiComponentParagraph class="name">
            {{
              testimonial.customer.name
            }}
          </UiComponentParagraph>
          <UiComponentParagraph class="position">
            {{
              testimonial.customer.position
            }}
          </UiComponentParagraph>
          <UiComponentParagraph class="compact-infos">
            <span>{{ testimonial.customer.name }}</span>, {{ testimonial.customer.position }}
          </UiComponentParagraph>
        </div>
      </div>
      <UiComponentImage
        v-if="variant === 'compact'"
        :alt="testimonial.logo.alt"
        class="logo"
        contains
        :expected-size="32"
        :height="testimonial.logo.height"
        orientation="portrait"
        :source="testimonial.logo.url"
        :width="testimonial.logo.width"
      />
    </div>
  </div>
</template>

<script setup>
// Composables
const { locale } = useI18n()

// Props
const props = defineProps({
  testimonial: {
    required: true,
    type: Object,
  },
  variant: {
    default() {
      return 'default'
    },
    required: false,
    type: String,
    validator(value) {
      return ['default', 'compact'].indexOf(value) !== -1
    },
  },
  withStars: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
})

// Computed Properties
const avatarSize = computed(() => {
  return props.variant === 'compact' ? 32 : 48
})

const quotationMarkStart = computed(() => {
  return locale.value === 'fr' ? '« ' : '"'
})

const quotationMarkEnd = computed(() => {
  return locale.value === 'fr' ? ' »' : '"'
})
</script>

<style lang="postcss" scoped>
.testimonial {
  @apply flex flex-col w-full p-8 py-10;

  @screen md {
    @apply p-12;
  }

  @screen xl {
    @apply py-10 px-12;
  }

  .head {
    .logo {
      @apply h-8 rounded-none;
    }
  }

  .rating {
    @apply flex flex-row mt-4;
  }

  .rating + .body {
    @apply mt-4;
  }

  .body {
    @apply mt-6 text-grey-blue-600;
  }

  :deep(.button) {
    @apply justify-start mt-4 px-0 h-auto font-normal text-livestorm-blue-700 leading-7 !important;

    .icon {
      @apply text-livestorm-blue-700;
    }
  }

  .footer {
    @apply flex flex-row mt-7;

    &.compact {
      @screen xl {
        @apply mt-5;
      }
    }

    .customer {
      @apply flex;

      .left {
        @apply w-12 h-12 rounded-full overflow-hidden;
      }

      .right {
        @apply flex flex-col justify-center ml-3;

        .name {
          @apply text-winter-green-900 font-medium;
        }

        .position {
          @apply text-grey-blue-600 mt-0;
        }

        .compact-infos {
          @apply hidden;
        }
      }
    }
  }

  &.compact {
    @apply p-8 border-grey-blue-200;
    border-radius: 24px;
    border-width: 3px;

    .head {
      @screen md {
        @apply hidden;
      }
    }

    .body {
      @apply mt-4 text-base leading-7;

      @screen md {
        @apply mt-0;
      }
    }

    .footer {
      @apply mt-4 justify-between;

      .customer {
        @apply items-center;

        .left {
          @apply w-8 h-8;
        }

        .right {
          .name {
            @apply font-medium;
          }

          .name,
          .position {
            @apply text-base;
            line-height: 21px;

            @screen md {
              @apply hidden;
            }
          }

          .compact-infos {
            @apply hidden text-grey-blue-600 leading-7 text-base;

            @screen md {
              @apply block;
            }

            span {
              @apply font-medium text-grey-blue-900;
            }
          }
        }
      }

      .logo {
        @apply hidden;

        @screen md {
          @apply flex rounded-none;
        }
      }
    }
  }
}
</style>
